import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import {
  AlertTriangle,
  Calendar,
  CheckCircle2,
  ClipboardCheck,
  CreditCard,
  ExternalLink,
  Globe,
  Mail,
  Send,
  User,
} from 'lucide-react';
import {
  UniversalPage,
  UniversalLoadingState,
  Card,
  Button,
  Input,
  StatusBadge,
  type StatusTone,
  NotificationCard,
  useToast,
} from '@sighthost/ui';
import { supabase } from '@/shared/lib/supabase';
import { formatDateTime, formatZARFromCents } from '@/shared/utils/format';

/**
 * /admin/services/:id  —  Service order detail + lifecycle actions
 *
 * Phase 10 Slice 10.2, T-10.2-10.
 *
 * Shows a service_order row with:
 *   - Order summary (SKU, customer, payment, plan, bundle)
 *   - Linked ticket and invoice
 *   - Three lifecycle action buttons:
 *     * Mark consultation complete  → service-order-mark-brief-complete EF
 *       (Studio Starter only; transitions awaiting_consultation/paid → in_progress
 *        and starts the 48-working-hour SLA clock)
 *     * Mark delivered              → service-order-mark-delivered EF
 *       (Studio Starter only; transitions in_progress → delivered, optionally
 *        enqueues whm.createacct for free-hosting auto-attach)
 *     * Mark complete               → service-order-mark-complete EF
 *       (any SKU; transitions to complete and closes linked ticket)
 *
 * Each button is enabled/disabled based on sku_code + current status.
 * Idempotency keys are generated client-side per click (UUID v4) and sent
 * via the `Idempotency-Key` HTTP header.
 *
 * Optimistic UI: on EF success, local state is updated immediately (no reload).
 * On error, a NotificationCard renders the error code + message.
 *
 * Spec: _bmad-output/PHASE_10_SERVICES_ARCHITECTURE.md §2.5–§2.7
 *       _bmad-output/PHASE_10_SERVICES_BRD.md §5.6 (wireframe)
 */

interface ServiceOrderDetail {
  id: string;
  sku_code: string;
  status: string;
  customer_id: string;
  primary_fqdn: string | null;
  preview_url: string | null;
  assignee_user_id: string | null;
  deposit_amount_cents: number | null;
  plan_months: number | null;
  total_paid_cents: number;
  brief_completed_at: string | null;
  delivered_at: string | null;
  complete_at: string | null;
  slo_credit_issued_at: string | null;
  created_at: string;
  invoice_id: string | null;
  // Joined / derived
  sku_display_name: string;
  sku_description: string | null;
  sku_price_cents: number;
  customer_email: string | null;
  invoice_number: string | null;
  invoice_paid_at: string | null;
  ticket_id: string | null;
  installment: {
    plan_months: number;
    installments_paid: number;
    installments_total: number;
    installment_cents: number;
  } | null;
}

const STATUS_TONE: Record<string, StatusTone> = {
  pending_payment: 'pending',
  paid: 'paid',
  awaiting_consultation: 'pending',
  in_progress: 'info',
  delivered: 'info',
  complete: 'active',
  failed_terminal: 'failed',
  cancelled: 'failed',
};

function uuid(): string {
  return crypto.randomUUID();
}

export default function ServiceOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const [order, setOrder] = useState<ServiceOrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<null | 'brief' | 'delivered' | 'complete'>(null);
  const [primaryFqdnDraft, setPrimaryFqdnDraft] = useState('');
  const [previewUrlDraft, setPreviewUrlDraft] = useState('');

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      // 1. Service order + service_sku join
      const { data: orderRow, error: oErr } = await supabase
        .from('service_order')
        .select(
          `
          id, sku_code, status, customer_id, primary_fqdn, preview_url, assignee_user_id,
          deposit_amount_cents, plan_months, total_paid_cents,
          brief_completed_at, delivered_at, complete_at, slo_credit_issued_at,
          created_at, invoice_id,
          service_sku:sku_code ( display_name, description, price_cents )
        `,
        )
        .eq('id', id)
        .maybeSingle();

      if (oErr) throw oErr;
      if (!orderRow) {
        setError('Service order not found.');
        setLoading(false);
        return;
      }

      const skuJoin = (orderRow as unknown as { service_sku: { display_name: string; description: string | null; price_cents: number } | null }).service_sku;

      // 2. Customer email — prefer customer_profile.billing_email, fall back to nothing.
      let customerEmail: string | null = null;
      const { data: profile } = await supabase
        .from('customer_profile')
        .select('billing_email')
        .eq('id', orderRow.customer_id)
        .maybeSingle();
      if (profile && (profile as { billing_email: string | null }).billing_email) {
        customerEmail = (profile as { billing_email: string }).billing_email;
      }

      // 3. Invoice (paid_at + number)
      let invoiceNumber: string | null = null;
      let invoicePaidAt: string | null = null;
      if (orderRow.invoice_id) {
        const { data: inv } = await supabase
          .from('invoice')
          .select('number, paid_at')
          .eq('id', orderRow.invoice_id)
          .maybeSingle();
        if (inv) {
          invoiceNumber = (inv as { number: string }).number;
          invoicePaidAt = (inv as { paid_at: string | null }).paid_at;
        }
      }

      // 4. Linked ticket id — recovered from the saga's audit_log row.
      let ticketId: string | null = null;
      const { data: auditRows } = await supabase
        .from('audit_log')
        .select('after')
        .eq('entity_kind', 'service_order')
        .eq('entity_id', orderRow.id)
        .eq('event', 'service_order.payment_received')
        .order('occurred_at', { ascending: false })
        .limit(1);
      if (auditRows && auditRows.length > 0) {
        const after = (auditRows[0] as { after: { ticket_id?: string } | null }).after;
        if (after && typeof after.ticket_id === 'string') ticketId = after.ticket_id;
      }

      // 5. Installment schedule (Studio Starter plans only)
      let installment: ServiceOrderDetail['installment'] = null;
      if (orderRow.sku_code === 'studio-starter' && orderRow.plan_months) {
        const { data: inst } = await supabase
          .from('installment_schedule')
          .select('plan_months, installments_paid, installments_total, installment_cents')
          .eq('service_order_id', orderRow.id)
          .maybeSingle();
        if (inst) installment = inst as ServiceOrderDetail['installment'];
      }

      const detail: ServiceOrderDetail = {
        id: orderRow.id,
        sku_code: orderRow.sku_code,
        status: orderRow.status,
        customer_id: orderRow.customer_id,
        primary_fqdn: orderRow.primary_fqdn,
        preview_url: orderRow.preview_url,
        assignee_user_id: orderRow.assignee_user_id,
        deposit_amount_cents: orderRow.deposit_amount_cents,
        plan_months: orderRow.plan_months,
        total_paid_cents: orderRow.total_paid_cents,
        brief_completed_at: orderRow.brief_completed_at,
        delivered_at: orderRow.delivered_at,
        complete_at: orderRow.complete_at,
        slo_credit_issued_at: orderRow.slo_credit_issued_at,
        created_at: orderRow.created_at,
        invoice_id: orderRow.invoice_id,
        sku_display_name: skuJoin?.display_name ?? orderRow.sku_code,
        sku_description: skuJoin?.description ?? null,
        sku_price_cents: skuJoin?.price_cents ?? 0,
        customer_email: customerEmail,
        invoice_number: invoiceNumber,
        invoice_paid_at: invoicePaidAt,
        ticket_id: ticketId,
        installment,
      };

      setOrder(detail);
      // Never pre-fill the domain: a Studio order with a domain was usually
      // provisioned when it was paid, and re-sending it would ask for a second
      // cPanel account (AR-OI-8). Staff type it deliberately at go-live.
      setPrimaryFqdnDraft('');
      setPreviewUrlDraft(detail.preview_url ?? '');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const isStudio = order?.sku_code === 'studio-starter';
  const canMarkBrief = useMemo(() => {
    if (!order || !isStudio) return false;
    return order.status === 'paid' || order.status === 'awaiting_consultation';
  }, [order, isStudio]);
  const canMarkDelivered = useMemo(() => {
    if (!order || !isStudio) return false;
    return order.status === 'in_progress' || order.status === 'awaiting_consultation';
  }, [order, isStudio]);
  const canMarkComplete = useMemo(() => {
    if (!order) return false;
    if (order.status === 'complete') return false;
    if (order.status === 'cancelled' || order.status === 'failed_terminal') return false;
    // Care + Email Setup go straight from paid → complete; Studio needs delivered first.
    if (isStudio) return order.status === 'delivered';
    return order.status === 'paid';
  }, [order, isStudio]);

  async function callAction(
    action: 'brief' | 'delivered' | 'complete',
    fnName: string,
    body: Record<string, unknown>,
    successCopy:
      | { title: string; body: string }
      | ((data: unknown) => { title: string; body: string }),
  ) {
    if (!order) return;
    setBusy(action);
    const idempotencyKey = uuid();
    try {
      const { data, error: efErr } = await supabase.functions.invoke(fnName, {
        body: { service_order_id: order.id, ...body },
        headers: { 'idempotency-key': idempotencyKey },
      });
      if (efErr) {
        // Try to parse the EF's structured error envelope.
        let msg = efErr.message;
        try {
          const ctx = (efErr as unknown as { context?: Response }).context;
          if (ctx && typeof ctx.json === 'function') {
            const parsed = (await ctx.clone().json()) as { code?: string; message?: string };
            if (parsed?.message) msg = parsed.message;
            else if (parsed?.code) msg = parsed.code;
          }
        } catch {
          // fall through with original message
        }
        throw new Error(msg);
      }
      const copy = typeof successCopy === 'function' ? successCopy(data) : successCopy;
      toast({ tone: 'success', title: copy.title, body: copy.body });
      // Re-fetch to capture any timestamp / status changes the EF made.
      await load();
      void data;
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Action failed',
        body: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setBusy(null);
    }
  }

  async function onMarkBrief() {
    await callAction('brief', 'service-order-mark-brief-complete', {}, {
      title: 'Brief marked complete',
      body: 'SLA clock started — 48 working hours to first preview.',
    });
  }

  async function onMarkDelivered() {
    if (!order) return;
    const fqdn = primaryFqdnDraft.trim();
    const preview = previewUrlDraft.trim() || order.preview_url || '';
    const extra: Record<string, unknown> = {};
    if (fqdn) extra.primary_fqdn = fqdn;
    if (preview) extra.preview_url = preview;
    await callAction('delivered', 'service-order-mark-delivered', extra, (data) => {
      // Report what the Edge Function actually did, not what was asked for.
      const queued = !!(data as { provisioning_job_id?: string | null } | null)?.provisioning_job_id;
      const hosting = queued ? ` Free-hosting account queued for ${fqdn}.` : '';
      return {
        title: 'Marked delivered — preview sent',
        body: preview
          ? `Customer emailed the review link (${preview}).${hosting || ' Their domain is untouched until they approve.'}`
          : fqdn
          ? `No preview link given, so the email points at ${fqdn}.${hosting}`
          : 'Order marked delivered. No preview link and no domain — the email will have no link.',
      };
    });
  }

  async function onMarkComplete() {
    await callAction('complete', 'service-order-mark-complete', {}, {
      title: 'Order marked complete',
      body: 'Linked ticket closed. Customer notified.',
    });
  }

  if (loading) {
    return (
      <UniversalPage
        title="Service order"
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: 'Services', href: '/admin/services' },
          { label: 'Detail' },
        ]}
      >
        <UniversalLoadingState variant="spinner" caption="Loading service order…" />
      </UniversalPage>
    );
  }

  if (error || !order) {
    return (
      <UniversalPage
        title="Service order"
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: 'Services', href: '/admin/services' },
          { label: 'Not found' },
        ]}
      >
        <NotificationCard
          tone="danger"
          title="Could not load service order"
          body={error ?? 'No record matched that ID.'}
        />
      </UniversalPage>
    );
  }

  return (
    <UniversalPage
      title={`${order.sku_display_name} — ${order.id.slice(0, 8)}`}
      description={
        order.invoice_number
          ? `Invoice ${order.invoice_number} · ${order.customer_email ?? order.customer_id.slice(0, 8)}`
          : (order.customer_email ?? order.customer_id.slice(0, 8))
      }
      breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: 'Services', href: '/admin/services' },
        { label: order.id.slice(0, 8) },
      ]}
      meta={
        <div className="flex flex-wrap items-center gap-3">
          <StatusBadge tone={STATUS_TONE[order.status] ?? 'pending'}>
            {order.status.replace(/_/g, ' ')}
          </StatusBadge>
          {order.assignee_user_id ? (
            <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
              <User className="h-3.5 w-3.5" />
              Assigned: {order.assignee_user_id.slice(0, 8)}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-sm text-warning-dark">
              <User className="h-3.5 w-3.5" />
              Unassigned
            </span>
          )}
        </div>
      }
    >
      <section className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Summary card */}
        <Card interactive={false} className="lg:col-span-2">
          <header className="border-b border-border px-6 py-3">
            <h2 className="font-display text-sm font-semibold text-ink">Summary</h2>
          </header>
          <dl className="grid grid-cols-1 divide-y divide-border sm:grid-cols-2 sm:divide-x sm:divide-y-0">
            <SummaryRow icon={Calendar} label="Created" value={formatDateTime(order.created_at)} />
            <SummaryRow
              icon={CreditCard}
              label="Paid so far"
              value={
                <span className="tabular-nums">
                  {formatZARFromCents(order.total_paid_cents)} / {formatZARFromCents(order.sku_price_cents)}
                </span>
              }
            />
            {order.invoice_paid_at && (
              <SummaryRow
                icon={CheckCircle2}
                label="First payment"
                value={formatDateTime(order.invoice_paid_at)}
              />
            )}
            {order.primary_fqdn && (
              <SummaryRow icon={Globe} label="Primary FQDN" value={order.primary_fqdn} />
            )}
            {order.brief_completed_at && (
              <SummaryRow
                icon={ClipboardCheck}
                label="Brief complete"
                value={formatDateTime(order.brief_completed_at)}
              />
            )}
            {order.delivered_at && (
              <SummaryRow icon={Send} label="Delivered" value={formatDateTime(order.delivered_at)} />
            )}
            {order.complete_at && (
              <SummaryRow
                icon={CheckCircle2}
                label="Complete"
                value={formatDateTime(order.complete_at)}
              />
            )}
            {order.slo_credit_issued_at && (
              <SummaryRow
                icon={AlertTriangle}
                label="SLO credit issued"
                value={formatDateTime(order.slo_credit_issued_at)}
              />
            )}
          </dl>

          {order.sku_description && (
            <div className="border-t border-border px-6 py-4 text-sm leading-relaxed text-muted-foreground">
              {order.sku_description}
            </div>
          )}

          {order.installment && (
            <div className="border-t border-border px-6 py-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                <span className="text-primary">02/</span> Installment plan
              </p>
              <p className="mt-2 text-sm text-ink">
                {order.installment.plan_months}-month plan ·{' '}
                <span className="tabular-nums">
                  {formatZARFromCents(order.installment.installment_cents)}
                </span>
                /mo · {order.installment.installments_paid} of{' '}
                {order.installment.installments_total} cycles paid
              </p>
            </div>
          )}
        </Card>

        {/* Sidebar — links + actions */}
        <div className="space-y-6">
          <Card interactive={false}>
            <header className="border-b border-border px-6 py-3">
              <h2 className="font-display text-sm font-semibold text-ink">Links</h2>
            </header>
            <ul className="divide-y divide-border">
              {order.ticket_id && (
                <li className="px-6 py-3">
                  <Link
                    to={`/admin/support/${order.ticket_id}`}
                    className="inline-flex items-center gap-2 text-sm text-primary-ink hover:underline"
                  >
                    <Mail className="h-4 w-4" />
                    Ticket {order.ticket_id.slice(0, 8)}
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                </li>
              )}
              {order.invoice_id && (
                <li className="px-6 py-3">
                  <Link
                    to={`/admin/billing?invoice=${order.invoice_id}`}
                    className="inline-flex items-center gap-2 text-sm text-primary-ink hover:underline"
                  >
                    <CreditCard className="h-4 w-4" />
                    Invoice {order.invoice_number ?? order.invoice_id.slice(0, 8)}
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                </li>
              )}
              <li className="px-6 py-3">
                <Link
                  to={`/admin/customers/${order.customer_id}`}
                  className="inline-flex items-center gap-2 text-sm text-primary-ink hover:underline"
                >
                  <User className="h-4 w-4" />
                  Customer
                  <ExternalLink className="h-3 w-3" />
                </Link>
              </li>
            </ul>
          </Card>

          <Card interactive={false}>
            <header className="border-b border-border px-6 py-3">
              <h2 className="font-display text-sm font-semibold text-ink">Actions</h2>
            </header>
            <div className="space-y-3 px-6 py-4">
              {isStudio && (
                <ActionButton
                  icon={ClipboardCheck}
                  label="Mark consultation complete"
                  hint="Starts the 48-working-hour SLA clock."
                  onClick={onMarkBrief}
                  disabled={!canMarkBrief}
                  loading={busy === 'brief'}
                />
              )}

              {isStudio && (
                <div className="space-y-2">
                  {/* 'Delivered' means PREVIEW SENT (locked 2026-07-26). The
                      customer reviews on a temporary address; their domain —
                      and the email running on it — only moves once they
                      approve. Preview link first because it is the field used
                      at this stage; the domain is filled in later, at go-live. */}
                  <Input
                    placeholder="Preview link (e.g. acme.accounts-75b.workers.dev)"
                    value={previewUrlDraft}
                    onChange={(e) => setPreviewUrlDraft(e.target.value)}
                    disabled={busy !== null || !canMarkDelivered}
                  />
                  <Input
                    placeholder="Customer's own domain — leave blank until go-live"
                    value={primaryFqdnDraft}
                    onChange={(e) => setPrimaryFqdnDraft(e.target.value)}
                    disabled={busy !== null || !canMarkDelivered}
                  />
                  <p className="text-xs text-muted-foreground">
                    Put the review address in <strong>Preview link</strong>. The domain field
                    queues a free-hosting cPanel account, so fill it in only once the customer
                    has approved and you are ready to point their domain at the site.
                  </p>
                  <ActionButton
                    icon={Send}
                    label="Mark delivered — send preview"
                    hint="Emails the customer their review link. Their domain is not touched."
                    onClick={onMarkDelivered}
                    disabled={!canMarkDelivered}
                    loading={busy === 'delivered'}
                  />
                </div>
              )}

              <ActionButton
                icon={CheckCircle2}
                label="Mark complete"
                hint={
                  isStudio
                    ? 'Closes the order + linked ticket. Customer gets the studio-complete email.'
                    : 'Closes the order + linked ticket.'
                }
                onClick={onMarkComplete}
                disabled={!canMarkComplete}
                loading={busy === 'complete'}
              />

              {!isStudio && (
                <p className="text-xs text-muted-foreground">
                  Consultation / delivery actions only apply to Studio Starter orders.
                </p>
              )}
            </div>
          </Card>
        </div>
      </section>
    </UniversalPage>
  );
}

function SummaryRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 px-6 py-4">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      <div className="min-w-0 flex-1">
        <dt className="text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
        <dd className="mt-0.5 text-sm text-ink">{value}</dd>
      </div>
    </div>
  );
}

function ActionButton({
  icon: Icon,
  label,
  hint,
  onClick,
  disabled,
  loading,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  hint: string;
  onClick: () => void;
  disabled: boolean;
  loading: boolean;
}) {
  return (
    <div>
      <Button
        type="button"
        variant="primary"
        size="default"
        onClick={onClick}
        disabled={disabled || loading}
        loading={loading}
        className="w-full justify-start"
      >
        <Icon className="h-4 w-4" /> {label}
      </Button>
      <p className="mt-1.5 text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}
